import type {
  AccountingOAuthStatusRequest,
  AccountingOAuthStatusResult,
  ConnectAccountingOAuthRequest,
  ConnectAccountingOAuthResult,
  DisconnectAccountingOAuthRequest,
  GetActiveAccountingSystemRequest,
  GetActiveAccountingSystemResult,
} from "@src/domain/dto/accountingOAuth";
import type { IAccountingOAuthRepository } from "@src/domain/repositories/accountingOAuth";
import { AdminRestApiPaths, PrivateHttp } from "@src/infrastructure/adapters/http";

// PrivateHttp unwraps the API envelope (`{ data, message }`) via NetworkManager
// + HttpService, so these types describe the inner `data` payload.
interface ConnectAccountingOAuthApiResponse {
  authorizationUrl: string;
}

interface AccountingOAuthStatusApiResponse {
  connected: boolean;
  accountingSystemKey: string;
  realmId: number | null;
}

interface ActiveAccountingSystemApiResponse {
  accountingSystemKey: string | null;
}

export class AccountingOAuthRepository implements IAccountingOAuthRepository {
  async connect(
    params: ConnectAccountingOAuthRequest,
  ): Promise<ConnectAccountingOAuthResult> {
    const response = await new PrivateHttp().post<ConnectAccountingOAuthApiResponse>(
      AdminRestApiPaths.accountingOAuthConnect,
      {
        brand_key: params.brandKey,
        accounting_system_key: params.accountingSystemKey,
      },
    );

    const authorizationUrl = response?.authorizationUrl;
    if (!authorizationUrl) {
      throw new Error("Authorization URL was not returned by the server");
    }

    return { authorizationUrl };
  }

  async disconnect(params: DisconnectAccountingOAuthRequest): Promise<void> {
    await new PrivateHttp().post(AdminRestApiPaths.accountingOAuthDisconnect, {
      brand_key: params.brandKey,
      accounting_system_key: params.accountingSystemKey,
    });
  }

  async getStatus(
    params: AccountingOAuthStatusRequest,
  ): Promise<AccountingOAuthStatusResult> {
    const response = await new PrivateHttp().post<AccountingOAuthStatusApiResponse>(
      AdminRestApiPaths.accountingOAuthStatus,
      {
        brand_key: params.brandKey,
        accounting_system_key: params.accountingSystemKey,
      },
    );

    if (!response) {
      throw new Error("Accounting OAuth status was not returned by the server");
    }

    return {
      connected: Boolean(response.connected),
      accountingSystemKey: response.accountingSystemKey,
      realmId: response.realmId ?? null,
    };
  }

  async getActiveSystem(
    params: GetActiveAccountingSystemRequest,
  ): Promise<GetActiveAccountingSystemResult> {
    const response = await new PrivateHttp().post<ActiveAccountingSystemApiResponse>(
      AdminRestApiPaths.accountingOAuthActiveSystem,
      {
        brand_key: params.brandKey,
      },
    );

    return {
      accountingSystemKey: response?.accountingSystemKey ?? null,
    };
  }
}
